// Alpine component for the cart's "Apply this design to another product" modal.
// Loaded once from layout/theme.liquid <head> and exposed as window.applyArtworkModal
// so the modal section's `x-data="applyArtworkModal()"` resolves on every Alpine
// evaluation, including after Barba.js SPA soft-nav swaps the cart HTML.

// Per-handle thumbnail URL resolver. The modal section liquid populates
// window.__APPLY_ARTWORK_THUMBS from the same theme settings the customizer
// uses, so swapping between the picker and /pages/customizer shows matching
// artwork. The hardcoded CDN URL is a last-resort fallback for environments
// where the section script hasn't loaded (rare — would also leave the
// customizer's own picker broken).
const _thumb = (handle) => window.__APPLY_ARTWORK_THUMBS?.[handle] || '';

const buildProducts = () => [
  {
    handle: 'snapback',
    modelName: 'Snapback',
    title: 'Snapback',
    priceRange: '$14.00 - $28.00',
    image: _thumb('snapback'),
    pattern: 'Embroidery',
    color: { name: 'Black', hex: '#000000' }
  },
  {
    handle: 'foam-trucker',
    modelName: 'Foam Trucker',
    title: 'Foam Trucker',
    priceRange: '$14.00 - $28.00',
    image: _thumb('foam-trucker'),
    pattern: 'Embroidery',
    color: { name: 'Black', hex: '#000000' }
  },
  {
    handle: 'class-trucker',
    modelName: 'Class Trucker',
    title: 'Class Trucker',
    priceRange: '$14.00 - $28.00',
    image: _thumb('class-trucker'),
    pattern: 'Embroidery',
    color: { name: 'Black', hex: '#000000' }
  },
  {
    handle: 'dad-hat',
    modelName: 'Dad Hat',
    title: 'Dad Hat',
    priceRange: '$14.00 - $28.00',
    image: _thumb('dad-hat'),
    pattern: 'Embroidery',
    color: { name: 'Black', hex: '#000000' }
  },
  {
    handle: 't-shirt',
    modelName: 'T-Shirt',
    title: 'T-Shirt',
    priceRange: '$14.00 - $28.00',
    image: _thumb('t-shirt'),
    pattern: 'Print',
    color: { name: 'black', hex: '#000000' }
  }
];

// Customer-facing sizes — must match the set we expose on the customizer
// page and in the saved-designs picker. Apliiq's REST catalog has a 4XL
// variant but we don't sell it (no demand + their stock is patchy), so the
// modal picker excludes it.
const TSHIRT_SIZES = ['S', 'M', 'L', 'XL', '2XL', '3XL'];
const TSHIRT_DEFAULT_SIZE = 'L';

// Shopify T-shirt variant titles use XXL/XXXL, but TSHIRT_SIZES (and the
// backend size names) use 2XL/3XL. A size arriving from a cart line item
// (variant_options[0] / the title's trailing token) is therefore in the
// XXL/XXXL form and won't match TSHIRT_SIZES, silently falling back to the
// default. Map it onto the canonical 2XL/3XL. Idempotent: 2XL/3XL and
// S/M/L/XL pass through unchanged.
const normalizeTshirtSize = (s) => {
  const v = (s == null ? '' : String(s)).trim().toUpperCase();
  if (v === 'XXL') return '2XL';
  if (v === 'XXXL') return '3XL';
  return v;
};

const sampleUrl = () => window.__APPLY_ARTWORK_SAMPLE__
  || '/cdn/shop/files/apply-artwork-modal.sample-design.json';

const applyArtworkModalFactory = () => ({
  show: false,
  products: buildProducts(),
  selectedHandle: null,
  isReady: false,
  isSaving: false,
  // Per-button spinner: only the clicked button shows progress.
  _action: null, // 'addToCart' | 'customize' | null
  // Handle of the cart line item's current model — its row is locked in the
  // picker and the action buttons stay disabled until the user picks another.
  _sourceHandle: null,
  _engine: null,
  _openDetail: null,
  _sourceConfig: null,   // { canvas, pattern, color, model } from fetchProductConfig
  errorMessage: '',
  tshirtSizes: TSHIRT_SIZES,
  selectedSize: TSHIRT_DEFAULT_SIZE,

  get selectedProduct() {
    return this.products.find((p) => p.handle === this.selectedHandle) || null;
  },

  init() {},

  async openModal(detail) {
    this._openDetail = detail || {};
    this.show = true;
    document.body.classList.add('apply-artwork-modal-open');
    this.isReady = false;
    this.isSaving = false;
    this.errorMessage = '';
    // Pre-select the size the customer already has in cart (for T-shirt
    // sources), so "apply to T-shirt" defaults to the same size by default.
    // Try the explicit currentSize first (banner sends variant_options[0]);
    // fall back to parsing the trailing token of currentTitle ("...T-Shirt - L"
    // -> "L") so this still works when the banner snippet hasn't propagated.
    const titleSizeGuess = normalizeTshirtSize(
      (this._openDetail.currentTitle || '').split(' - ').pop()
    );
    const currentSize = normalizeTshirtSize(this._openDetail.currentSize);
    this.selectedSize = TSHIRT_SIZES.includes(currentSize)
      ? currentSize
      : (TSHIRT_SIZES.includes(titleSizeGuess) ? titleSizeGuess : TSHIRT_DEFAULT_SIZE);
    // T-shirt detection: apliiq_type='dtgprint' from the source line item is
    // the primary signal (banner snippet sends it through), because T-shirts
    // ride on the Class Trucker mock 3D model so config.model alone would
    // point us at the hat. Fallback to title pattern matching if the banner
    // snippet hasn't been redeployed yet (edge cache on .liquid files can
    // hold the old version for a while after `shopify theme push`).
    const sourceIsTShirt =
      this._openDetail.apliiqType === 'dtgprint'
      || /t-?shirt/i.test(this._openDetail.currentTitle || '');
    // Seed selection from the cart line item — for T-shirts force the T-Shirt
    // product entry regardless of title; for hats fall back to title match.
    const seedMatch = sourceIsTShirt
      ? this.products.find((p) => p.handle === 't-shirt')
      : (this._openDetail.currentTitle
        ? this.products.find((p) => p.title === this._openDetail.currentTitle)
        : null);
    this.selectedHandle = seedMatch ? seedMatch.handle : null;
    this._sourceHandle = seedMatch ? seedMatch.handle : null;

    try {
      await this._ensureEngine();

      const config = await this._engine.fetchProductConfig(this._openDetail.productId);
      this._sourceConfig = config;
      if (!config) {
        this.errorMessage =
          "Couldn't load this design. The server is unavailable — try again later.";
      }

      // For T-shirts, do NOT use config.model to switch selection — that path
      // would land us on Class Trucker (the mock). Keep the T-Shirt entry that
      // the seed step already selected. For hats, config.model is reliable.
      const matched = sourceIsTShirt
        ? this.products.find((p) => p.handle === 't-shirt')
        : (config?.model
          ? this.products.find((p) => p.modelName === config.model)
          : null);
      this.selectedHandle = matched ? matched.handle : this.products[0].handle;
      if (matched) this._sourceHandle = matched.handle;

      const product = this.selectedProduct;
      this._engine.selectProduct(product.modelName, {
        pattern: config?.pattern || product.pattern,
        color: config?.color || product.color
      });

      // New-format T-shirt configs (Task 18+) store the front canvas under
      // `canvas_front`; legacy designs use a single `canvas`. extractFrontCanvas
      // handles both shapes — apply-artwork only ever previews/transfers the
      // front side per spec decision #4 (back artwork never copies).
      const designJson = this._engine.extractFrontCanvas(config) || (await this._loadDesignJson());
      await this._engine.loadDesign(designJson);
      this.isReady = true;
      // Two rAFs so x-show finishes display:none→flex and the canvas reports
      // its real size before freezeCamera reads container dimensions.
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      this._engine.freezeCamera();
    } catch (e) {
      console.error('[apply-artwork] open failed', e);
      this.errorMessage = 'Failed to load the 3D preview. Close and try again.';
      this.isReady = true;
    }
  },

  async selectProduct(handle) {
    this.selectedHandle = handle;
    if (!this._engine) return;
    const product = this.selectedProduct;
    if (!product) return;
    this._engine.selectProduct(product.modelName, {
      pattern: this._openDetail?.currentPattern || product.pattern,
      color: this._openDetail?.currentColor || product.color
    });
  },

  setSize(size) {
    this.selectedSize = size;
  },

  // Resolve apliiq product+color IDs for the selected target from HATS_IDS
  // (T-Shirt has its own entry there alongside the hats).
  _resolveApliiqIds() {
    const target = this.selectedProduct;
    if (!target || !this._engine?.HATS_IDS) return null;
    const sourceColorName = this._sourceConfig?.color?.name || '';
    const sourceColorLc = sourceColorName.toLowerCase();
    // T-Shirt cart row uses target.handle === 't-shirt'; map it to the
    // 'T-Shirt' name we use in HATS_IDS.
    const targetName = target.handle === 't-shirt' ? 'T-Shirt' : target.modelName;

    const targetEntry = this._engine.HATS_IDS.find((h) => h.name === targetName);
    if (!targetEntry) return null;
    const matchedColor =
      targetEntry.Colors.find((c) => c.Name.toLowerCase() === sourceColorLc)
      || targetEntry.Colors[0];
    if (!matchedColor) return null;
    if (matchedColor.Name.toLowerCase() !== sourceColorLc) {
      console.warn(
        `[apply-artwork] source color "${sourceColorName}" not in ${targetName}, falling back to "${matchedColor.Name}"`
      );
    }
    return {
      apliiq_product_id: String(targetEntry.Id),
      apliiq_color_id: String(matchedColor.Id),
    };
  },

  async _buildSharedPayload() {
    // Use the LIVE fabric canvas state (which drawer.setProductType already
    // rescaled to the target product's canvas dims) instead of the original
    // source-product config. Otherwise a T-shirt → hat transfer persists
    // T-shirt-coord objects (1800×2070) on the new hat (1800×900) and the
    // print lands off-area when Apply Artwork is reopened on that hat.
    // Fallback to the source config for the degenerate case where the live
    // canvas is somehow unavailable (engine not mounted).
    const sourceCanvas = this._engine.getCurrentCanvasJSON()
      || this._engine.extractFrontCanvas(this._sourceConfig);
    if (!this._sourceConfig || !sourceCanvas) return null;
    const target = this.selectedProduct;
    const isTShirt = target?.handle === 't-shirt';
    const ids = this._resolveApliiqIds();
    if (!ids) return null;
    const image_product = await this._engine.freezeAndScreenshot();
    const image_artwork = this._engine.getArtworkBase64();

    let sizeId = null;
    if (isTShirt) {
      const tshirtEntry = this._engine.HATS_IDS.find((h) => h.name === 'T-Shirt');
      const sizeOpt = (tshirtEntry?.Sizes || []).find((s) => s.Name === this.selectedSize);
      if (sizeOpt) sizeId = sizeOpt.Id;
    }

    return {
      ids,
      image_product,
      image_artwork,
      // T-shirts go through DTG; hats keep the existing print/embroidery logic.
      type: isTShirt
        ? 'dtgprint'
        : (this._sourceConfig.pattern === 'Print' ? 'transfer_print' : 'embroidery'),
      targetModelName: isTShirt ? 'Classic Streetwear T-Shirt' : target.modelName,
      sourceColor: this._sourceConfig.color,
      sourcePattern: this._sourceConfig.pattern,
      sourceCanvas,
      sizeId,
    };
  },

  // POST /products/create with the shared payload. Returns the parsed JSON
  // on success or null on any failure (errorMessage is set).
  async _createShopifyProduct(shared) {
    const productPayload = {
      apliiq_product_id: shared.ids.apliiq_product_id,
      apliiq_color_id: shared.ids.apliiq_color_id,
      type: shared.type,
      price: 0,
      description: '',
      name: shared.targetModelName,
      image_product: shared.image_product,
      image_artwork: shared.image_artwork,
      data: {
        pattern: shared.sourcePattern,
        model: shared.targetModelName,
        color: shared.sourceColor,
        qty: 1,
        canvas: shared.sourceCanvas,
      },
      ...(shared.sizeId != null ? { size_id: shared.sizeId } : {}),
    };

    const res = await fetch(`${this._engine.BACKEND_BASENAME_API}/products/create`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...this._engine.backendHeaders() },
      body: JSON.stringify(productPayload),
    });
    if (!res.ok) {
      this.errorMessage = "Couldn't create product. Try again.";
      return null;
    }
    const json = await res.json();
    if (!json?.shopify_product_id || !json?.shopify_variant_id) {
      this.errorMessage = "Couldn't create product. Try again.";
      return null;
    }
    return json;
  },

  async addToCart() {
    if (!this._engine || !this.isReady || this.isSaving) return;
    this.isSaving = true;
    this._action = 'addToCart';
    this.errorMessage = '';
    try {
      const shared = await this._buildSharedPayload();
      if (!shared) {
        this.errorMessage = "Couldn't prepare this design. Try again.";
        return;
      }

      const created = await this._createShopifyProduct(shared);
      if (!created) return;

      if (typeof window._cart?.add !== 'function') {
        this.errorMessage = "Cart helper unavailable. Reload the page.";
        return;
      }
      // Backend already picked the variant matching `shared.sizeId` (for
      // T-shirts) — the resulting Shopify variant_title is the canonical
      // size source for the Apliiq webhook.
      await window._cart.add({
        items: [{
          id: Number(created.shopify_variant_id),
          quantity: 1,
          properties: { apliiq_type: shared.type },
        }],
      });

      // Mirror the customizer's add-to-cart behavior: capture this
      // just-created design into Saved Designs so it shows up in the
      // customer's portal before checkout. Best-effort — never block or
      // fail the cart add over it.
      await this._autoSaveDesign(shared, created);

      if (!this.show) return;
      this.close();
    } catch (e) {
      console.error('[apply-artwork] addToCart failed', e);
      this.errorMessage = 'Network error. Try again.';
    } finally {
      this.isSaving = false;
      this._action = null;
    }
  },

  // Create a Saved Designs row for the design we just added to cart, carrying
  // the freshly created shopify_product_id. The order webhook flips this row
  // to 'ordered' at checkout via `WHERE shopify_product_id = ?`, so it will
  // never produce a duplicate. Logged-in only: the saved-designs API requires
  // customer headers (and the webhook skips guests too). Best-effort — this
  // method never throws, so a failure can't break the cart add that preceded
  // it. Apply-artwork always builds a NEW product, so there is no prior row to
  // dedupe against; we always create exactly one.
  async _autoSaveDesign(shared, created) {
    const customer = window.SHOPIFY_CUSTOMER;
    if (!customer) return; // guest → nothing to attach the design to
    try {
      const payload = {
        // Normalize type_hat the same way the customizer save does so the
        // saved-designs Buy/Edit flows route T-shirts vs hats correctly.
        type_hat: shared.type === 'dtgprint' ? 'T-Shirt' : shared.targetModelName,
        color: shared.sourceColor?.name || '',
        type_print: shared.sourcePattern,
        name: shared.targetModelName,
        price: 0,

        apliiq_product_id: shared.ids.apliiq_product_id,
        apliiq_color_id: +shared.ids.apliiq_color_id,

        shopify_product_id: created.shopify_product_id,
        shopify_variant_id: created.shopify_variant_id,
        description: '',
        image_product: shared.image_product,
        ...(shared.image_artwork ? { image_artwork: shared.image_artwork } : {}),
        ...(shared.sizeId != null ? { size_id: shared.sizeId } : {}),
        // Same config blob _createShopifyProduct persists, so the saved row is
        // re-openable in the customizer's edit flow.
        data: {
          pattern: shared.sourcePattern,
          model: shared.targetModelName,
          color: shared.sourceColor,
          qty: 1,
          canvas: shared.sourceCanvas,
        },
      };

      await fetch(`${this._engine.BACKEND_BASENAME_API}/saved-designs`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Customer-Id': customer.id,
          'Customer-Email': customer.email,
          'Sig': customer.sig,
          ...this._engine.backendHeaders(),
        },
        body: JSON.stringify(payload),
      });
    } catch (e) {
      console.error('[apply-artwork] auto-save design skipped:', e);
    }
  },

  // Variant B: create a Shopify product with the source artwork applied to
  // the target hat, then redirect into the customizer in edit-mode. Anon-OK,
  // the customizer's own Add to Cart commits to cart. Orphans are reaped by
  // clean_abandoned_products_task on the backend.
  async editArtwork() {
    if (!this._engine || !this.isReady || this.isSaving) return;
    this.isSaving = true;
    this._action = 'customize';
    this.errorMessage = '';
    try {
      const shared = await this._buildSharedPayload();
      if (!shared) {
        this.errorMessage = "Couldn't prepare this design. Try again.";
        return;
      }

      const created = await this._createShopifyProduct(shared);
      if (!created) return;

      // The customizer reads ?type= to drive its right-side panel, drawer
      // selection, and 3D model lookup. Without it, the customizer defaults
      // to 'Class Trucker', so a T-shirt redirect lands on the hat panel.
      // For T-shirts append &type=T-Shirt; hats use their model name (matches
      // the existing /pages/customizer?type=<HatName> contract elsewhere).
      // For T-shirts also forward &size= so the customizer's initTShirtSizes
      // picks the same size the customer chose in the modal — otherwise the
      // hardcoded Liquid default ('L') wins on page load.
      const target = this.selectedProduct;
      const isTShirt = target?.handle === 't-shirt';
      const typeParam = isTShirt
        ? 'T-Shirt'
        : (target?.modelName || '');
      const params = new URLSearchParams();
      params.set('id', String(created.shopify_product_id));
      if (typeParam) params.set('type', typeParam);
      if (isTShirt && this.selectedSize) params.set('size', this.selectedSize);
      window.location.href = '/pages/customizer?' + params.toString();
    } catch (e) {
      console.error('[apply-artwork] editArtwork failed', e);
      this.errorMessage = 'Network error. Try again.';
    } finally {
      this.isSaving = false;
      this._action = null;
    }
  },

  close() {
    // Don't dispose the engine — disposing the WebGL/Fabric contexts means
    // the next open would have to rebuild them from scratch. Hiding the modal
    // is enough; the engine stays alive between opens on this page.
    this.show = false;
    document.body.classList.remove('apply-artwork-modal-open');
  },

  async _ensureEngine() {
    const container = document.querySelector('[data-apply-artwork-canvas-mount]');
    const canvas = document.querySelector('#apply-artwork-3d-canvas');
    if (!container || !canvas) {
      throw new Error(
        '[apply-artwork] modal section not in DOM — make sure the '
        + 'modal_apply_artwork_to_product section is included in the current template.'
      );
    }
    // Dynamic import with a literal path → Vite splits the 3D engine into a
    // separate chunk that's only fetched the first time the modal opens. The
    // cart page stays light until the user actually clicks the banner.
    const mod = await import(window.__AA_ENGINE_URL__);

    // After Barba.js soft-nav, the cached engine module may still hold an
    // Editor bound to a canvas that's no longer in the document — dispose so
    // the next mount creates a fresh Editor bound to our current canvas.
    const mountedCanvas = mod.getMountedCanvas?.();
    if (mountedCanvas && (mountedCanvas !== canvas || !document.body.contains(mountedCanvas))) {
      try { mod.dispose(); } catch (e) { console.warn('[apply-artwork] dispose failed', e); }
    }

    if (!mod.getMountedCanvas?.()) {
      await mod.mount({ canvas, container });
    }

    this._engine = mod;
    return mod;
  },

  async _loadDesignJson() {
    try {
      const res = await fetch(sampleUrl(), { credentials: 'omit' });
      if (!res.ok) throw new Error(`sample design fetch failed: ${res.status}`);
      return await res.json();
    } catch (e) {
      console.warn('[apply-artwork] falling back to inline sample', e);
      return { version: '5.3.0', objects: [], background: 'rgba(0,0,0,0)' };
    }
  }
});

// Bundle loaded as an ES module → may resolve after Alpine starts processing
// x-data. Register both via Alpine.data() on alpine:init (preferred path,
// independent of load order) and keep the window global for any x-data that
// uses the call form `applyArtworkModal()`.
window.applyArtworkModal = applyArtworkModalFactory;
function registerAlpineData() {
  if (window.Alpine && typeof window.Alpine.data === 'function') {
    window.Alpine.data('applyArtworkModal', applyArtworkModalFactory);
  }
}
document.addEventListener('alpine:init', registerAlpineData);
registerAlpineData(); // covers the case where Alpine already booted
